import React, { useEffect, useRef, useState } from 'react';
import T from 'prop-types';
import { createPortal } from 'react-dom';
// components
import { useIntl } from 'react-intl';
import { ReactComponent as DockIcon } from 'assets/svg/dock-right.svg';
import { ReactComponent as UndockIcon } from 'assets/svg/undock.svg';
// utils
import classnames from 'classnames/bind';
// styles
import styles from './IntercomWindow.module.scss';

const cn = classnames.bind(styles);

const FRAME_SELECTOR = '.intercom-messenger-frame';
const LAUNCHER_SELECTOR = '.intercom-launcher-frame';
// Page content the docked window makes room for (the scroll container of MainLayout)
const CONTENT_SELECTOR = '#bodybag';
// The layout's desktop breakpoint, below it the content doesn't make room (MainLayout.scss)
const DOCK_MEDIA_QUERY = '(min-width: 1024px)';
const DOCKED_CLASS = 'is-intercom-docked';
const DOCK_WIDTH_PROPERTY = '--intercom-dock-width';
const DOCK_TOP_PROPERTY = '--intercom-dock-top';
// Narrower page content isn't worth docking for, the window floats over the page instead
const MIN_CONTENT_WIDTH = 600;
// Dropped this close to its own corner, the window goes back to Intercom's place and docks again
const SNAP_DISTANCE = 48;
const GRIP_WIDTH = 64;
const TOGGLE_WIDTH = 24;
const HANDLE_HEIGHT = 24;
const HANDLE_SPACE = HANDLE_HEIGHT + 8; // handle plus the gap to the frame
const LAUNCHER_GAP = 16;
const CLOSED_SYNC_INTERVAL = 250;
const OPEN_IDLE_SYNC_INTERVAL = 100;
// Frames without any layout change before the open window is polled on the slow interval
const STABLE_FRAMES_BEFORE_IDLE = 10;
const KEY_STEP = 16;
const KEY_DIRECTIONS = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
};

// Intercom animates the frame with `transform`, so the separate `translate` property is used to move it
const isTranslateSupported = !!(window.CSS && window.CSS.supports && window.CSS.supports('translate', '1px 1px'));
const dockMedia = window.matchMedia(DOCK_MEDIA_QUERY);

// Top-left corner the user moved the window to, kept outside the component so it survives reopening.
// null while the user hasn't moved it: Intercom places the window and the open one docks
let framePosition = null;
let positionOwnerId = null;
let safeTop = 0;
let appliedDock = null;
// Where Intercom puts the frame on its own, seen whenever the window isn't docked (closed too).
// Moves out of the dock are clamped against it rather than the stretched docked frame
let floatingFrame = null;
const elements = {};

const getElement = (selector) => {
  if (!elements[selector] || !elements[selector].isConnected) elements[selector] = document.querySelector(selector);
  return elements[selector];
};

// Notch / status bar inset of an installed app
const getSafeTop = () => {
  const probe = document.createElement('div');
  probe.style.cssText = 'position: fixed; top: env(safe-area-inset-top, 0px); visibility: hidden;';
  document.body.appendChild(probe);
  const { offsetTop } = probe;
  document.body.removeChild(probe);
  return offsetTop;
};

// offset* of a fixed element ignore transforms, so they give the place Intercom put it in
const getBox = (element) => ({
  left: element.offsetLeft,
  top: element.offsetTop,
  width: element.offsetWidth,
  height: element.offsetHeight,
});

// clientWidth/clientHeight match the area fixed elements are laid out in, innerWidth shrinks on pinch-zoom
const getLayout = (frame) => {
  const launcher = getElement(LAUNCHER_SELECTOR);
  return {
    frame: getBox(frame),
    viewport: { width: document.documentElement.clientWidth, height: document.documentElement.clientHeight },
    launcher: launcher && launcher.offsetWidth ? getBox(launcher) : null,
  };
};

// Full screen on phones: there is nowhere to move it and nothing to make room for
const isFrameFullScreen = ({ frame, viewport }) => frame.width >= viewport.width || frame.height >= viewport.height;

// Closed frame stays in the DOM, hidden by opacity or visibility. Center alignment shifts the open
// frame with a transform that offset* can't see
const isFrameOpen = (frame) => {
  const { visibility, opacity, transform } = window.getComputedStyle(frame);
  return visibility !== 'hidden' && Number(opacity) > 0 && transform === 'none' && frame.offsetWidth > 0;
};

const isOverlapping = (a, b, gap) => (
  a.left < b.left + b.width + gap && b.left - gap < a.left + a.width
  && a.top < b.top + b.height + gap && b.top - gap < a.top + a.height
);

const getDistance = (a, b) => Math.abs(a.left - b.left) + Math.abs(a.top - b.top);

const isNear = (a, b) => Math.abs(a.left - b.left) <= SNAP_DISTANCE && Math.abs(a.top - b.top) <= SNAP_DISTANCE;

// Keeps the moved frame on screen and clear of the launcher, which doesn't move with it.
// On a low screen the frame stays fully visible and the handle overlaps its top edge instead
const clampFramePosition = ({ left, top }, { frame, viewport, launcher }) => {
  const box = {
    ...frame,
    left: Math.max(0, Math.min(viewport.width - frame.width, left)),
    top: Math.min(viewport.height - frame.height, Math.max(safeTop + HANDLE_SPACE, top)),
  };
  if (!launcher || !isOverlapping(box, launcher, LAUNCHER_GAP)) return box;

  const freePlaces = [
    { ...box, top: launcher.top - LAUNCHER_GAP - frame.height },
    { ...box, left: launcher.left - LAUNCHER_GAP - frame.width },
  ].filter((place) => place.top >= safeTop + HANDLE_SPACE && place.left >= 0);
  if (!freePlaces.length) return box;
  return freePlaces.reduce((closest, place) => (getDistance(place, box) < getDistance(closest, box) ? place : closest));
};

// Compared against the inline style itself: Intercom rewrites the frame's style on open / close
// and would drop the property, a cache wouldn't notice
const setTranslate = (frame, x, y) => {
  const value = x || y ? `${x}px ${y}px` : '';
  if (frame.style.getPropertyValue('translate') === value) return;
  if (value) {
    frame.style.setProperty('translate', value);
  } else {
    frame.style.removeProperty('translate');
  }
};

// The column is the frame plus Intercom's padding on both sides (measured, so any workspace
// setup docks), the same padding above it leaves room for the handle
const getDock = ({ frame, viewport }) => {
  const content = getElement(CONTENT_SELECTOR);
  if (!content || !dockMedia.matches) return null;
  const padding = Math.max(0, viewport.width - frame.left - frame.width);
  const width = frame.width + padding * 2;
  // The content is already narrowed by the current dock, so its full width is measured back
  const contentWidth = content.offsetWidth + (appliedDock ? appliedDock.width : 0);
  if (contentWidth - width < MIN_CONTENT_WIDTH) return null;
  return { width, top: padding + HANDLE_SPACE };
};

const isSameDock = (prev, next) => (
  prev === next || (!!prev && !!next && prev.width === next.width && prev.top === next.top)
);

// The layout, modals and the media viewer read the custom property, the stylesheet stretches the frame
const setDock = (dock) => {
  if (isSameDock(appliedDock, dock)) return;
  appliedDock = dock;
  const { style, classList } = document.documentElement;
  if (dock) {
    style.setProperty(DOCK_WIDTH_PROPERTY, `${dock.width}px`);
    style.setProperty(DOCK_TOP_PROPERTY, `${dock.top}px`);
    classList.add(DOCKED_CLASS);
  } else {
    style.removeProperty(DOCK_WIDTH_PROPERTY);
    style.removeProperty(DOCK_TOP_PROPERTY);
    classList.remove(DOCKED_CLASS);
  }
};

const getLayoutSignature = ({ frame, viewport, launcher }, box) => [
  frame.left, frame.top, frame.width, frame.height,
  viewport.width, viewport.height,
  launcher && [launcher.left, launcher.top, launcher.width, launcher.height],
  box.left, box.top,
].join();

// Whether the open window sits in its column ('docked'), could be put there ('dockable') or has no room
const getHandleWidth = (dockMode) => GRIP_WIDTH + (dockMode === 'none' ? 0 : TOGGLE_WIDTH);

const isSameHandle = (prev, next) => (
  prev === next
  || (!!prev && !!next && prev.left === next.left && prev.top === next.top && prev.dockMode === next.dockMode)
);

// Arrows with modifiers are browser shortcuts (back / forward), leave them alone
const getKeyDirection = (e) => (e.altKey || e.ctrlKey || e.metaKey ? undefined : KEY_DIRECTIONS[e.key]);

// Places the Intercom messenger window: docked beside the page content on a wide screen, floating
// once the user drags it by the grab bar above it, docked again when dropped back in its corner.
// The button on the bar toggles the two states without a drag
const IntercomWindow = ({ userId }) => {
  const { formatMessage } = useIntl();
  const [handle, setHandle] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const handleRef = useRef(null);
  const layoutRef = useRef(null);
  const dragStartRef = useRef(null);
  const wakeRef = useRef(null);

  const endDrag = () => {
    if (!dragStartRef.current) return;
    dragStartRef.current = null;
    setIsDragging(false);
  };

  // Next user on the same device gets the window in its default place
  useEffect(() => {
    if (userId === positionOwnerId) return;
    positionOwnerId = userId;
    framePosition = null;
  }, [userId]);

  useEffect(() => {
    if (!isTranslateSupported) return undefined;
    let timerId;
    let animationFrameId;
    let isIdle = false;
    let stableFrames = 0;
    let lastSignature = '';

    // setState is called only on a real change, so idle ticks don't pile up updates
    const showHandle = (next) => {
      if (isSameHandle(handleRef.current, next)) return;
      handleRef.current = next;
      setHandle(next);
    };

    // Returns whether the window is open and movable
    const sync = () => {
      const frame = getElement(FRAME_SELECTOR);
      const layout = frame && getLayout(frame);
      if (!frame || !layout || isFrameFullScreen(layout)) {
        // The stored position is kept and comes back once the frame is a pop-up again
        if (frame) setTranslate(frame, 0, 0);
        setDock(null);
        return false;
      }
      const isOpen = isFrameOpen(frame);
      if (!appliedDock) floatingFrame = layout.frame;
      const dock = getDock(layout);
      // The docked frame is measured again on the next tick, once the stylesheet has stretched it
      setDock(isOpen && !framePosition ? dock : null);
      // Applied while closed too, so the window opens right where the user left it
      const box = framePosition ? clampFramePosition(framePosition, layout) : layout.frame;
      const x = Math.round(box.left - layout.frame.left);
      const y = Math.round(box.top - layout.frame.top);
      setTranslate(frame, x, y);
      if (!isOpen) return false;

      const signature = getLayoutSignature(layout, box);
      stableFrames = signature === lastSignature ? stableFrames + 1 : 0;
      lastSignature = signature;

      layoutRef.current = { ...layout, box };
      let dockMode = 'none';
      if (dock) dockMode = framePosition ? 'dockable' : 'docked';
      showHandle({
        left: Math.round(box.left + (box.width - getHandleWidth(dockMode)) / 2),
        top: Math.round(Math.max(safeTop, box.top - HANDLE_SPACE)),
        dockMode,
      });
      return true;
    };

    // Every animation frame while the open window is moving (expand / collapse animation, drag,
    // resize), a slow poll once it has settled so an idle chat doesn't cost a layout read per frame
    const tick = () => {
      isIdle = false;
      if (sync()) {
        if (stableFrames < STABLE_FRAMES_BEFORE_IDLE) {
          animationFrameId = window.requestAnimationFrame(tick);
        } else {
          isIdle = true;
          timerId = window.setTimeout(tick, OPEN_IDLE_SYNC_INTERVAL);
        }
        return;
      }
      layoutRef.current = null;
      showHandle(null);
      endDrag();
      timerId = window.setTimeout(tick, CLOSED_SYNC_INTERVAL);
    };

    // Back to per-frame syncing right away, a pending animation frame picks the change up itself
    const wake = () => {
      stableFrames = 0;
      if (!isIdle) return;
      window.clearTimeout(timerId);
      animationFrameId = window.requestAnimationFrame(tick);
      isIdle = false;
    };
    wakeRef.current = wake;

    // The inset only changes with the orientation, resize events are too frequent for a layout probe
    const onOrientationChange = () => {
      safeTop = getSafeTop();
      wake();
    };

    onOrientationChange();
    tick();
    window.addEventListener('resize', wake);
    window.addEventListener('orientationchange', onOrientationChange);
    return () => {
      wakeRef.current = null;
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timerId);
      window.removeEventListener('resize', wake);
      window.removeEventListener('orientationchange', onOrientationChange);
      const frame = getElement(FRAME_SELECTOR);
      if (frame) setTranslate(frame, 0, 0);
      setDock(null);
    };
  }, []);

  const getFloatingLayout = (layout) => (floatingFrame ? { ...layout, frame: floatingFrame } : layout);

  const wake = () => {
    if (wakeRef.current) wakeRef.current();
  };

  // The stored position is clamped on every move, so it never drifts outside the screen
  const moveFrame = (left, top) => {
    const layout = layoutRef.current;
    if (!layout) return;
    const box = clampFramePosition({ left, top }, getFloatingLayout(layout));
    framePosition = { left: box.left, top: box.top };
    wake();
  };

  // Back to Intercom's own place, where the open window docks when it fits
  const resetFrame = () => {
    framePosition = null;
    wake();
  };

  // Out of the column into Intercom's own corner, floating over the page like before
  const undockFrame = () => {
    const home = floatingFrame || (layoutRef.current && layoutRef.current.box);
    if (!home) return;
    framePosition = { left: home.left, top: home.top };
    wake();
  };

  const onToggleDock = () => {
    if (handleRef.current && handleRef.current.dockMode === 'docked') {
      undockFrame();
    } else {
      resetFrame();
    }
  };

  // Dropped close to its corner, the window is handed back to Intercom rather than parked next to
  // it. Only after a drag: a keyboard step out of the dock would snap straight back
  const snapFrame = () => {
    const layout = layoutRef.current;
    if (framePosition && layout && isNear(framePosition, getFloatingLayout(layout).frame)) resetFrame();
  };

  const onPointerDown = (e) => {
    const layout = layoutRef.current;
    if (e.button !== 0 || dragStartRef.current || !layout) return;
    e.preventDefault();
    // Capture keeps pointer events coming while the cursor is over the Intercom iframe
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStartRef.current = {
      pointerId: e.pointerId,
      clientX: e.clientX,
      clientY: e.clientY,
      left: layout.box.left,
      top: layout.box.top,
    };
    setIsDragging(true);
  };

  const onPointerMove = (e) => {
    const dragStart = dragStartRef.current;
    if (!dragStart || e.pointerId !== dragStart.pointerId) return;
    moveFrame(dragStart.left + e.clientX - dragStart.clientX, dragStart.top + e.clientY - dragStart.clientY);
  };

  const onPointerEnd = (e) => {
    const dragStart = dragStartRef.current;
    if (!dragStart || e.pointerId !== dragStart.pointerId) return;
    endDrag();
    snapFrame();
  };

  const onKeyDown = (e) => {
    const layout = layoutRef.current;
    if (!layout) return;
    if (e.key === 'Home') {
      e.preventDefault();
      e.stopPropagation();
      resetFrame();
      return;
    }
    const direction = getKeyDirection(e);
    if (!direction) return;
    e.preventDefault();
    e.stopPropagation();
    // Counted from the last requested place, so fast key repeats inside one frame aren't lost
    const from = framePosition ? clampFramePosition(framePosition, getFloatingLayout(layout)) : layout.box;
    moveFrame(from.left + direction.x * KEY_STEP, from.top + direction.y * KEY_STEP);
  };

  // Global keyup listeners shouldn't react to the keys used here
  const onKeyUp = (e) => {
    if (e.key === 'Home' || getKeyDirection(e)) e.stopPropagation();
  };

  if (!handle) return null;

  const { dockMode } = handle;
  const dragLabel = formatMessage({ id: 'component.intercomDragHandle.dragToMove' });
  const toggleLabel = formatMessage({
    id: dockMode === 'docked' ? 'component.intercomDragHandle.undock' : 'component.intercomDragHandle.dockToSide',
  });
  const ToggleIcon = dockMode === 'docked' ? UndockIcon : DockIcon;

  return createPortal(
    <div
      className={cn('intercom-drag-handle')}
      style={{ left: handle.left, top: handle.top, width: getHandleWidth(dockMode), height: HANDLE_HEIGHT }}
    >
      <button
        type="button"
        className={cn('grip-button', { 'is-dragging': isDragging })}
        title={dragLabel}
        aria-label={dragLabel}
        aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight Home"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onLostPointerCapture={onPointerEnd}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
      >
        <span className={cn('grip')} />
      </button>
      {dockMode !== 'none' && (
        <button
          type="button"
          className={cn('toggle-button')}
          title={toggleLabel}
          aria-label={toggleLabel}
          onClick={onToggleDock}
        >
          <ToggleIcon className={cn('toggle-icon')} />
        </button>
      )}
    </div>,
    document.body
  );
};

IntercomWindow.propTypes = {
  userId: T.oneOfType([T.number, T.string]),
};

IntercomWindow.defaultProps = {
  userId: null,
};

export default IntercomWindow;
